import {parseAxiosPromise} from '@/axiosInstance'
import {QUERY_KEYS} from '@/queryClient'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {captureException} from '@sentry/react'
import {httpGetAslByCity} from './address.http'
import {AslDistrict, PaginationResponse, ResponseType} from '@/types'
import {z} from 'zod'
import {useInputSearch} from '@/hooks/useInputSearch'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

export const HttpGetAslByCityResponse = z.object({
    data: z.array(AslDistrict),
    meta: PaginationResponse
})
export type HttpGetAslByCityResponse = z.infer<typeof HttpGetAslByCityResponse>

export const useGetAslByCity = ({
    cityId,
    responseType,
    options
}: {
    cityId: string
    responseType: ResponseType
    options?: Pick<UseInfiniteQueryOptions<HttpGetAslByCityResponse>, 'enabled'>
}) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.ASL_BY_CITY, cityId, searchValue],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetAslByCity({
                    page: pageParam,
                    limit: 20,
                    cityId,
                    search: searchValue,
                    responseType
                }),
                responseShape: HttpGetAslByCityResponse,
                onZodError: captureException
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
